// Seeded onboarding rolls. Everything a new life depends on comes from the game seed,
// so a run is reproducible from seed + choices (needed for verified runs later).

import { TUNABLES } from "@degen/shared/config/tunables";
import { DISPLAY_NAME_POOL, HANDLE_POOL, PRESET_BIOS } from "@degen/shared/config/content";
import { moderateText } from "@degen/shared/moderation";
import { Rng, forkStream, mixSeed } from "./rng";
import type { Origin } from "./player";
import { generatePfpTraits, rarityOf, type PfpTraits, type RarityInfo } from "./pfp/traits";

export interface PfpCandidate {
  seed: number;
  traits: PfpTraits;
  rarity: RarityInfo;
}

/** The grid of PFPs offered on roll `roll` (0 = first grid, 1-2 = rerolls). */
export function pfpGrid(gameSeed: number, roll: number): PfpCandidate[] {
  const rng = new Rng(forkStream(gameSeed, `pfp-grid-${roll}`));
  const out: PfpCandidate[] = [];
  for (let i = 0; i < TUNABLES.pfp.gridSize; i++) {
    const seed = rng.seed();
    const traits = generatePfpTraits(seed);
    out.push({ seed, traits, rarity: rarityOf(traits) });
  }
  return out;
}

export function rollOrigin(gameSeed: number): Origin {
  return new Rng(forkStream(gameSeed, "origin")).weighted(TUNABLES.origins.weights) as Origin;
}

/** Random name/handle/bio suggestions. `n` lets the Randomize button cycle. */
export function randomIdentity(gameSeed: number, n: number) {
  const rng = new Rng(forkStream(gameSeed, `identity-${n}`));
  const i = rng.int(0, DISPLAY_NAME_POOL.length - 1);
  const suffix = rng.chance(0.4) ? String(rng.int(1, 99)) : "";
  return {
    name: DISPLAY_NAME_POOL[i],
    handle: (HANDLE_POOL[i] + suffix).slice(0, 15),
    bio: rng.pick(PRESET_BIOS),
  };
}

/** Outfit seed for the walking character; rerolling the look bumps `n`. */
export function outfitSeed(gameSeed: number, n: number): number {
  return mixSeed(gameSeed, 0x0f17 + n);
}

// ---------- validation (spec step 2) ----------

export function validName(name: string): string | null {
  const len = Array.from(name.trim()).length;
  if (len < 2) return "Name needs at least 2 characters.";
  if (len > 20) return "Max 20 characters.";
  const m = moderateText(name, "name", 20);
  return m.ok ? null : m.reason;
}

/** Lowercase and strip anything outside [a-z0-9_]. */
export function cleanHandle(raw: string): string {
  return raw.toLowerCase().replace(/[^a-z0-9_]/g, "").slice(0, 15);
}

export function validHandle(h: string): string | null {
  if (!/^[a-z0-9_]{3,15}$/.test(h)) return "Handle: 3-15 characters, a-z 0-9 _";
  // Same rules as the server, so other players see the handle you picked.
  const m = moderateText(h, "handle", 15);
  return m.ok ? null : m.reason;
}

export const BIO_MAX = 80;
